import * as _ from 'lodash';
import * as Edge from '../../../core';
import { ExerciseMetadata, ExerciseMetadataService } from '../../../services/exerciseMetadataService';

export interface FocusArea {
	title: string;
	colorClass: 'green' | 'blue' | 'purple';
	Icon: ExerciseMetadata['logo'];
}

const FOCUS_AREA_MESSAGES: Partial<Record<Edge.Models.ExerciseTypeId, string>> = {
	[Edge.Models.ExerciseTypeId.Alternating]: 'Improve near-to-far focus flexibility',
	[Edge.Models.ExerciseTypeId.ContrastSensitivity]: 'Improve visual detection in challenging conditions',
	[Edge.Models.ExerciseTypeId.ContrastTracking]: 'Improve tracking through changing visual conditions',
	[Edge.Models.ExerciseTypeId.Convergence]: 'Strengthen near visual focus and control',
	[Edge.Models.ExerciseTypeId.Depth]: 'Sharpen depth perception and spatial awareness',
	[Edge.Models.ExerciseTypeId.Divergence]: 'Strengthen distance visual focus and control',
	[Edge.Models.ExerciseTypeId.Pursuits]: 'Build smooth and consistent visual tracking',
	[Edge.Models.ExerciseTypeId.Recognition]: 'Increase visual processing speed and accuracy',
	[Edge.Models.ExerciseTypeId.Tracking]: 'Improve visual tracking speed and accuracy',
};

const FOCUS_AREA_COLORS: FocusArea['colorClass'][] = ['green', 'blue', 'purple'];

export const buildFocusAreas = (phases?: Edge.Models.PlanPhase[]): FocusArea[] => {
	if (!phases || phases.length === 0) {
		return [];
	}

	const configurations = _.flatMap(phases, (phase) => phase.exerciseConfigurations)
		.filter((config) => !!FOCUS_AREA_MESSAGES[config.exerciseTypeId])
		.sort((a, b) => a.exerciseTypeId.toString().localeCompare(b.exerciseTypeId.toString()));
	const counts = _.countBy(configurations, (config) => config.exerciseTypeId);
	const uniqueCounts = Object.entries(counts).reduce(
		(acc, [id, count]) => {
			const values = Object.values(acc);
			if (values.findIndex((v) => v === count) >= 0) {
				return acc;
			}
			return {
				...acc,
				[id]: count,
			};
		},
		{} as _.Dictionary<number>
	);

	return _.orderBy(Object.entries(uniqueCounts), ([, count]) => count, 'desc')
		.slice(0, 3)
		.reduce<FocusArea[]>((areas, [exerciseTypeIdKey]) => {
			const exerciseTypeId = exerciseTypeIdKey as Edge.Models.ExerciseTypeId;
			const message = FOCUS_AREA_MESSAGES[exerciseTypeId];
			const metadata = ExerciseMetadataService.getMetadata({ exerciseTypeId });
			if (!message || !metadata) {
				return areas;
			}
			areas.push({
				title: message,
				colorClass: FOCUS_AREA_COLORS[areas.length] || 'blue',
				Icon: metadata.logo,
			});
			return areas;
		}, []);
};
